import React, { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductHuntLaunchPage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      // Add to waitlist with Product Hunt tag
      await axios.post(`${API}/waitlist`, {
        email,
        source: 'producthunt',
        referral: 'producthunt-launch'
      });
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50">
      {/* Product Hunt Banner */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 px-4 text-center">
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <span className="text-2xl">🎉</span>
          <span className="font-medium">We're live on Product Hunt!</span>
          <a 
            href="https://www.producthunt.com/posts/adelevate"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 bg-white text-orange-600 px-3 py-1 rounded-full text-sm font-bold hover:bg-orange-100 transition ml-2"
          >
            <svg className="w-4 h-4" viewBox="0 0 40 40" fill="currentColor">
              <path d="M20 0C8.954 0 0 8.954 0 20s8.954 20 20 20 20-8.954 20-20S31.046 0 20 0zm0 36.8C10.726 36.8 3.2 29.274 3.2 20S10.726 3.2 20 3.2 36.8 10.726 36.8 20 29.274 36.8 20 36.8zm2.667-23.467H15.2V26.4h4.267v-5.333h3.2c3.533 0 6.4-2.134 6.4-5.867s-2.867-5.867-6.4-5.867zm0 8.534h-3.2v-5.334h3.2c1.6 0 2.667.8 2.667 2.667s-1.067 2.667-2.667 2.667z"/>
            </svg>
            Support us →
          </a>
        </div>
      </div>

      {/* Header */}
      <header className="py-6 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a href="/" className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
            AdElevate
          </a>
          <a 
            href="/"
            className="text-gray-600 hover:text-gray-900 font-medium"
          >
            ← Back to Home
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          {/* Product Hunt Badge */}
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-8">
            <svg className="w-5 h-5" viewBox="0 0 40 40" fill="currentColor">
              <path d="M20 0C8.954 0 0 8.954 0 20s8.954 20 20 20 20-8.954 20-20S31.046 0 20 0zm0 36.8C10.726 36.8 3.2 29.274 3.2 20S10.726 3.2 20 3.2 36.8 10.726 36.8 20 29.274 36.8 20 36.8zm2.667-23.467H15.2V26.4h4.267v-5.333h3.2c3.533 0 6.4-2.134 6.4-5.867s-2.867-5.867-6.4-5.867zm0 8.534h-3.2v-5.334h3.2c1.6 0 2.667.8 2.667 2.667s-1.067 2.667-2.667 2.667z"/>
            </svg>
            Featured on Product Hunt
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Create AI-Powered Ads
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-600">
              in 60 Seconds
            </span>
          </h1>
          
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Generate professional text, image, and video ads instantly. 
            No design skills needed. Export to Facebook, Instagram, Google, TikTok & more.
          </p>

          {/* Special Offer Box */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-1 max-w-xl mx-auto mb-12">
            <div className="bg-white rounded-xl p-6">
              <div className="flex items-center justify-center gap-2 mb-3">
                <span className="text-2xl">🎁</span>
                <span className="text-lg font-bold text-gray-900">Product Hunt Exclusive</span>
              </div>
              <p className="text-3xl font-bold text-orange-600 mb-2">
                100 FREE Credits
              </p>
              <p className="text-gray-600 text-sm">
                Sign up today and get 100 credits free (worth $20) — double our standard offer!
              </p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <a 
              href="/?ref=producthunt"
              className="px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold rounded-xl text-lg hover:from-orange-600 hover:to-orange-700 transition shadow-lg shadow-orange-500/25"
            >
              Claim 100 Free Credits →
            </a>
            <a 
              href="https://www.producthunt.com/posts/adelevate"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white border-2 border-orange-500 text-orange-600 font-bold rounded-xl text-lg hover:bg-orange-50 transition"
            >
              ⬆️ Upvote on Product Hunt
            </a>
          </div>
        </div>
      </section>

      {/* Demo Video Section */}
      <section className="py-16 px-4 bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            See AdElevate in Action
          </h2>
          
          {/* Video Player */}
          <div className="aspect-video bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl overflow-hidden border border-gray-700 shadow-2xl">
            <video 
              className="w-full h-full object-cover"
              controls
              autoPlay
              muted
              loop
              playsInline
              poster="/og-image.png"
            >
              <source 
                src="https://adelevate.s3.ap-southeast-2.amazonaws.com/users/332d1751-0ee9-4a72-b296-c1d640108f10/videos/2026/01/AdElevate_c02858b30_20260113_105252_06abe9cc.mp4" 
                type="video/mp4" 
              />
              Your browser does not support the video tag.
            </video>
          </div>
          
          <p className="text-center text-gray-400 mt-4 text-sm">
            Create professional ads in seconds — no design skills needed
          </p>
        </div>
      </section>

      {/* Product Hunt Exclusive Offer Banner */}
      <section className="py-8 px-4 bg-gradient-to-r from-orange-100 to-yellow-100">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <span className="text-2xl">🎁</span>
            <span className="font-bold text-gray-900">Product Hunt Exclusive:</span>
            <span className="text-gray-700">Sign up with Google or Email and get <strong>100 FREE credits</strong> (50 base + 50 bonus)</span>
            <a 
              href="/?ref=producthunt"
              className="px-4 py-2 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition text-sm"
            >
              Claim Now
            </a>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Why Makers Love AdElevate
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Built by makers, for makers. We know you need results fast without breaking the bank.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">60-Second Generation</h3>
              <p className="text-gray-600">
                From idea to ready-to-publish ad in under a minute. Text, image, and video ads.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Pay As You Go</h3>
              <p className="text-gray-600">
                No subscriptions required. Buy credits when you need them. Starting at just $0.20 per ad.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-7 h-7 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">50+ Languages</h3>
              <p className="text-gray-600">
                Create ads in any language. Perfect for global campaigns and multilingual audiences.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-7 h-7 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">AI Image Generation</h3>
              <p className="text-gray-600">
                Generate unique, eye-catching visuals. No stock photos or design skills needed.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">AI Video Ads</h3>
              <p className="text-gray-600">
                Create scroll-stopping video ads with AI. Perfect for TikTok, Reels, and Stories.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-7 h-7 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Multi-Platform Export</h3>
              <p className="text-gray-600">
                One-click export to Facebook, Instagram, Google, TikTok, LinkedIn, and more.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-12">
            Trusted by 10,000+ Marketers
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <p className="text-4xl font-bold text-orange-600">50K+</p>
              <p className="text-gray-600">Ads Generated</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-orange-600">10K+</p>
              <p className="text-gray-600">Happy Users</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-orange-600">60s</p>
              <p className="text-gray-600">Avg. Generation</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-orange-600">50+</p>
              <p className="text-gray-600">Languages</p>
            </div>
          </div>
        </div>
      </section>

      {/* Email Signup for Updates */}
      <section className="py-16 px-4">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Get Product Updates
          </h2>
          <p className="text-gray-600 mb-8">
            Be the first to know about new features and exclusive offers.
          </p>
          
          {!submitted ? (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-3 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition disabled:opacity-50"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
          ) : (
            <div className="bg-green-100 text-green-700 px-6 py-4 rounded-lg">
              🎉 Thanks for subscribing! Check your email for updates.
            </div>
          )}
          {error && <p className="text-red-500 mt-2">{error}</p>}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-4 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Create Your First AI Ad?
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Join 10,000+ marketers saving time and money with AI-powered advertising.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/?ref=producthunt"
              className="px-8 py-4 bg-white text-orange-600 font-bold rounded-xl text-lg hover:bg-gray-100 transition"
            >
              Get Started Free →
            </a>
            <a 
              href="https://www.producthunt.com/posts/adelevate"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-xl text-lg hover:bg-white/10 transition"
            >
              ⬆️ Upvote on Product Hunt
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p>&copy; 2026 AdElevate by 3STech. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <a href="/" className="hover:text-white transition">Home</a>
            <a href="/blog" className="hover:text-white transition">Blog</a>
            <a href="/credits" className="hover:text-white transition">Pricing</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProductHuntLaunchPage;
